/**
 * pages/checkins.ts —— 日常打卡（W2-7）
 * 来历：一期 [SEC:page-checkins] 的 checkinsHtml / [SEC:page-checkins-write] 的录入、详情、删除，
 *      渲染层重写、交互与版式不变。
 *
 * ## 与一期的五处差别（都不要"改回去"）
 * 1. **没有「活动类型」**（2026-08-20 删）：工具条没有类型下拉、卡片没有类型标签、
 *    录入弹窗没有类型与「具体类型」两个字段。**这是有意删的，不是漏的。**
 * 2. **筛选、计数、人次、两个时间窗统计全部由服务端算好下发**；前端一个都不许算（红线 4）。
 * 3. **照片走 COS 直传**（§五 5.8）。⚠ bucket 未开通期间是 **mock 模式**：
 *    不真的上传，只在当次页面里本地预览，刷新即无——弹窗里那行说明**必须留着**。
 * 4. **权限按 `createdBy`**：成员只维护自己创建的，队长维护本队全部，管理员维护全部。
 *    前端据此决定给不给「编辑 / 删除」，**但那只是显示**，拦截在服务端（红线 4）。
 * 5. **没有「导出 Excel」按钮**：Excel 导出属 P2（W4）。一期放了个 toast 占位是因为它是演示版；
 *    二期有真实排期，**先不放入口**，W4 落地时再加——点了只弹一句话的按钮比没有按钮更糟。
 */
import type { Activity, ActivityListResult, ActivityStats, Guest, Person, Team } from '@shared-types';
import { activities as actApi, persons as personApi, upload as uploadApi } from '../api/index.js';
import { errText } from '../api/client.js';
import { closeModal, confirmModal, emptyHtml, openModal, toast } from '../shared/common.js';
import { cut, esc, when } from '../shared/dom.js';
import { EIC, IC } from '../shared/icons.js';
import { state } from '../shared/state.js';
import * as meta from '../shared/meta.js';
import { refreshCurrent } from '../router/render.js';
import { definePage } from './types.js';

/** 一条记录最多 9 张照片。⚠ 服务端同样校验（`checkPhotos`），这里只是别让人白选 */
const MAX_PIC = 9;

/**
 * 活动摘要字数上限（2026-08-27 由 40 放宽到 200：40 字写不完"开了什么会 + 定了什么 + 留了什么"）。
 * 🔴 这里只是别让人白打，**校验在服务端** `activities/service.ts` 的 `checkSummary()`（红线 4），
 *   列宽在 `db/migrations/004_summary_200.sql`。**三处要一起改。**
 */
const SUM_MAX = 200;

const SPANS: ReadonlyArray<readonly [string, string]> = [
  ['14', '近 14 天'],
  ['cycle', '本周期内'],
  ['all', '全部'],
];

export interface CheckinsData {
  list: ActivityListResult;
  stats: ActivityStats;
  /**
   * personId → 姓名，详情弹窗要用（列表出参里只有 id）。
   * ⚠ 走 `GET /api/persons`（登录即可）而不是 `candidates`（本队权限）——
   *   打卡记录**全营公开**，别队的人也要能看到参与人姓名。
   */
  names: Record<string, string>;
}

/* ===================== 渲染（纯函数） ===================== */

const opt = (v: string, cur: string, lab: string): string =>
  `<option value="${esc(v)}"${v === cur ? ' selected' : ''}>${esc(lab)}</option>`;

/**
 * 能不能维护这条记录 = **创建人 / 本队任一成员 / 管理员**。
 *
 * 🔴 **2026-09-09 由「创建人 / 本队队长」放宽**（用户拍板）：管理员代录的打卡，
 *   本队队员也要能改。**放宽的前提是留痕**——详情弹窗左下角写着「谁 · 更新于什么时候」
 *   （见本文件的 `signatureText()`）。⚠ 两件事绑定，撤掉署名就要把权限收回去。
 * ⚠ 与服务端 `auth/guard.ts` 的 `canMaintainActivity()` **同一条判据**，改一处就要改两处。
 *   **这里只决定给不给按钮**，真正的拦截在服务端——前端隐藏不算权限（红线 4）。
 * ⚠ `teamIds` 只含**在用**队伍；`createdBy` 那一支不能删——创建人可能已经调离本队。
 */
function canMaintain(a: Activity): boolean {
  const u = meta.me();
  if (!u) return false;
  return u.isAdmin || u.teamIds.includes(a.teamId) || u.id === a.createdBy;
}

/** 队伍下拉：在用队伍 + 当前选中的归档队（从队伍详情带归档队进来时才出现，历史打卡要能查） */
function teamOptions(): Team[] {
  const L = meta.activeTeams().slice().sort((a, b) => (a.code < b.code ? -1 : 1));
  const cur = state.ck.team ? meta.teamById(state.ck.team) : null;
  if (cur && cur.archived) L.push(cur);
  return L;
}

const teamLabel = (t: Team | null): string => (t ? `${t.code} ${t.name}` : '未知队伍');

/**
 * 封面地址。⚠ `cover` 可能指向已被删掉的那一张，所以要夹到现有张数之内——
 *   这条夹取以前散在两个页面的 `load()` 里，2026-08-23 收成这一处。
 */
function coverUrl(a: Activity): string {
  if (!a.photos.length) return '';
  const key = a.photos[Math.min(a.cover, a.photos.length - 1)]?.objectKey;
  return key ? uploadApi.imgUrl(key, 'c') : '';
}

/**
 * 卡片渲染。
 * ⚠ **不再收 `urls` 上下文**（2026-08-23 图片提速）：封面地址由 `uploadApi.imgUrl()` 就地拼，
 *   页面不用先去问一趟地址——运营总览的「最近活动」复用这一个组件
 *   （基线 §5.1：同一种卡片不维护两套样式），两边都少一次往返。
 * ⚠ `opts.ops = false` 时不给编辑 / 删除按钮 —— 首页那份就是这么用的（一期同款）。
 */
export function cardHtml(a: Activity, opts: { ops?: boolean; eager?: boolean } = {}): string {
  const t = meta.teamById(a.teamId);
  const url = coverUrl(a);
  const pax = a.memberIds.length + a.guests.length;
  const ops = opts.ops !== false && canMaintain(a);

  /* 🔴 2026-08-24 卡片正文由三行压到两行：摘要与「参与 N 人」并到同一行（`.ck-r2`），
       「含临时 Y 人」整个去掉——临时参与人的明细在详情弹窗里，卡片上只报总人次。
     ⚠ `pax` 仍是 **正式 + 临时** 的合计，口径没变，变的只是不再拆开写。
     ⚠ 编辑 / 删除还留在自己的 `.ck-f` 那一行（只有打卡页会渲染；总览的卡片传 `ops:false`，
       所以总览上真的只有两行）。样式在 `24_checkin_card.css`。
     ⚠ 别把说明写进模板串里的 HTML 注释——那里的反引号会把模板字符串提前闭合。 */
  return `
  <article class="ck-card" data-act="${esc(a.id)}" data-ck="view|${esc(a.id)}" tabindex="0" role="button"
    aria-label="${esc(`${teamLabel(t)} ${a.date}：${a.summary}`)}，点击查看详情">
    <div class="ck-cover">
      ${
        url
          /* ⚠ `loading`：三列长列表，屏外的一律 lazy，否则一进页面全部并发下载。
               但**首屏那几张要 eager + fetchpriority=high**——它们本来就要显示，
               挂 lazy 只会让浏览器多绕一拍调度，首屏反而更晚出图。
             ⚠ `data-imgfb`：取不到图时把 img 摘掉、露出垫在下面的占位格
               （兜底监听在 `shared/events.ts`）——error 事件**不冒泡**，走不了页面那套事件委托。 */
          ? `<span class="ph" aria-hidden="true">${IC.pic}</span>
             <img src="${esc(url)}" alt="${esc(a.summary)}" decoding="async" data-imgfb
               ${opts.eager ? 'loading="eager" fetchpriority="high"' : 'loading="lazy"'}>`
          : `<span class="ph" aria-hidden="true">${IC.pic}</span>`
      }
      <span class="ck-cnt">${IC.cam}<span class="num">${a.photos.length}</span> 张照片</span>
    </div>
    <div class="ck-b">
      <div class="ck-h">
        <b title="${esc(t?.name ?? '')}"><i>${esc(t?.code ?? '')}</i>${esc(t?.name ?? '未知队伍')}</b>
        <span class="d">${esc(a.date.slice(5))}</span>
      </div>
      <div class="ck-r2">
        <p class="ck-s" title="${esc(a.summary)}">${esc(a.summary)}</p>
        <span class="ck-m">参与 <em class="num">${pax}</em> 人</span>
      </div>
      ${when(
        ops,
        `<div class="ck-f"><span class="ck-ops">
          <button class="tbtn" data-ck="edit|${esc(a.id)}">编辑</button>
          <button class="tbtn danger" data-ck="del|${esc(a.id)}">删除</button>
        </span></div>`,
      )}
    </div>
  </article>`;
}

export function gridHtml(d: CheckinsData): string {
  const rows = d.list.items;
  if (!rows.length) {
    const filtered = !!(state.ck.team || state.ck.q.trim() || state.ck.span !== '14');
    /* 两种空态说两句不同的话：**筛出来是空**和**一条都还没有**是两件事，
       说同一句会让人以为筛选坏了（一期同款）。 */
    return filtered
      ? emptyHtml(
          EIC.cal,
          '没有匹配的打卡记录',
          '试试放宽队伍或时间范围，或换个关键词',
          '<button class="tbtn" data-ck="clearf">清空筛选</button>',
        )
      : emptyHtml(
          EIC.cam,
          '近 14 天还没有打卡记录',
          '队伍完成活动后在这里录入，统计会自动汇总',
          `<button class="btn primary" data-ck="new">${IC.plus}新增打卡记录</button>`,
        );
  }
  // 首屏两行（三列 × 2）走 eager，其余 lazy —— 见 cardHtml 里那条 ⚠
  return `<div class="ck-grid">${rows.map((a, i) => cardHtml(a, { eager: i < 6 })).join('')}</div>
    <p class="okr-note" style="text-align:center;margin-top:16px">已显示全部 <span class="num">${rows.length}</span> 条</p>`;
}

export function checkinsHtml(d: CheckinsData): string {
  const cur = state.ck.team ? meta.teamById(state.ck.team) : null;
  // ⚠ 归档队【不能新增】打卡：它已退出本期考核，但历史仍可查（服务端也会拒）
  const lock = !!(cur && cur.archived);
  const st = d.stats;

  return `
  <div class="page">
    <div class="pagehead">
      <div>
        <h1>日常打卡</h1>
        <p class="ph-sub" id="ck-sub">近 14 天 <span class="num">${st.recent14.acts}</span> 条 ·
          <span class="num">${st.recent14.pax}</span> 人次 ·
          覆盖 <span class="num">${st.teamsCovered}</span> 支队伍</p>
      </div>
    </div>

    <div class="toolbar" style="margin-top:16px">
      <span class="ck-sel">队伍
        <select id="ck-team" aria-label="按队伍筛选">
          ${opt('', state.ck.team, '全部队伍')}${teamOptions()
            .map((t) => opt(t.id, state.ck.team, `${t.code} ${t.name}${t.archived ? '（已归档）' : ''}`))
            .join('')}
        </select></span>
      <span class="ck-sel">时间
        <select id="ck-span" aria-label="按时间段筛选">
          ${SPANS.map(([k, lab]) => opt(k, state.ck.span, lab)).join('')}
        </select></span>
      <span class="search">
        <span class="si" aria-hidden="true">${IC.search}</span>
        <input id="ck-q" type="text" value="${esc(state.ck.q)}" placeholder="搜摘要或参与人" aria-label="搜索打卡记录">
        ${when(!!state.ck.q, `<button class="sx" data-ck="clearq" aria-label="清空搜索">${IC.x}</button>`)}
      </span>
      <span class="cnt" id="ck-cnt">显示 <span class="num">${d.list.total} / ${d.list.visible}</span> 条 ·
        <span class="num">${d.list.pax}</span> 人次</span>
      <button class="btn primary" data-ck="new" ${
        lock ? 'disabled title="已归档队伍不能新增打卡"' : ''
      }>${IC.plus}新增打卡记录</button>
    </div>

    <div id="ck-grid">${gridHtml(d)}</div>
  </div>`;
}

/* ===================== 详情弹窗 ===================== */

/**
 * 打卡详情（一期 2026-08-04 评审后补的，卡片上放不下的东西都在这里）。
 * ⚠ 首页活动网格点卡片打开的**也是这一个弹窗**（W2-8 接入时直接复用），不要另写一份。
 */
/**
 * 打卡详情弹窗。
 * ⚠ 同 `cardHtml`：入参是**最小上下文**，运营总览点卡片时 import 它（W2-8）——
 *   同一条记录在两个页面上必须是同一个详情，不要写第二份。
 *
 * 🔴 **2026-08-22 改版**（用户反馈：信息重复、正文被元信息挤占）：
 *   队伍名进标题（`打卡详情 · 演示一队`），创建人与打卡时间进标题右侧小字，
 *   删掉正文顶部的队伍区块、底部的 `.dmeta`、footer 的跨队说明。
 *   ⚠ **活动日期随顶部区块一并移除**（那块整体被删）——若日后要找回，
 *     加回 `titleMeta` 里比重建一整块划算。
 *
 * 🔴 **九宫格开窗即渲染，不再"开窗后补拉地址"**（2026-08-23 图片提速）：
 *   地址现在是 `uploadApi.imgUrl()` 就地拼出来的，点开的**同一帧**九张 `<img>` 就都在 DOM 里了，
 *   浏览器立刻开始下——旧写法要等 `viewUrls` 回来才有 `<img>`，白丢一个往返。
 * ⚠ 九宫格用 `t` 变体（长边 176）：格子是 88×88，2× 屏也只要 176。
 *   实测同一张图 `t` 变体 2.3KB、原图 36KB，**差 15 倍**，而且肉眼没有区别。
 */
/**
 * 详情弹窗正文（纯函数，便于断言）。
 *
 * 🔴 **2026-08-27 改版**：不再拆「正式参与人员 / 临时参与人」两个区块，
 *   合成一个「参与人员（N 人）」——**正式在上一行、临时在下一行**，
 *   临时那行的 chip 写「姓名 · 单位」。
 *   理由：看的人关心的是"这场谁来了"，正式 / 临时是录入时的分类，不是阅读时的分类；
 *   拆成两块还要为"本次没有临时参与人"占一整行。
 *   ⚠ **口径没有变**：`N` 仍是正式 + 临时的人次合计（与卡片上的 `pax` 同源），
 *     临时参与人**依旧不进个人累计**（`RULES.md` §一，那是后端的事）。
 *   ⚠ 没有临时参与人时**整行不出现**，不留占位。
 */
export function viewBodyHtml(a: Activity, names: Record<string, string>): string {
  const nameOf = (id: string): string => names[id] ?? '（已删除人员）';
  const pics = a.photos.length
    ? a.photos
        .map(
          (p, i) => `<div class="ck-th ${i === a.cover ? 'on' : ''}">
        <button class="box" data-cklb="${i}" aria-label="放大第 ${i + 1} 张照片">${IC.pic}<img src="${esc(
          uploadApi.imgUrl(p.objectKey, 't'),
        )}" alt="照片 ${i + 1}"
          loading="lazy" decoding="async" data-imgfb>${
            i === a.cover ? '<span class="cv">封面</span>' : ''
          }</button>
      </div>`,
        )
        .join('')
    : '';

  return `
      <div class="flab">活动摘要</div>
      <p class="ftext">${esc(a.summary)}</p>
      <div class="flab">参与人员（<span class="num">${a.memberIds.length + a.guests.length}</span> 人）</div>
      <div class="ck-roster">
        <div class="roster">${
          a.memberIds.map((id) => `<span class="mchip">${esc(nameOf(id))}</span>`).join('') ||
          '<span class="mchip">未记录</span>'
        }</div>
        ${when(
          a.guests.length > 0,
          `<div class="roster tmp">${a.guests
            .map((g) => `<span class="mchip">${esc(`${g.name} · ${g.org}`)}</span>`)
            .join('')}</div>`,
        )}
      </div>
      <div class="flab">照片（<span class="num">${a.photos.length}</span> 张）<span class="fhint">点开可看大图</span></div>
      <div class="ck-thumbs">${pics}</div>`;
}

/**
 * 详情弹窗左下角那句署名：**谁 · 什么时候更新的**（2026-09-09 用户画的样子）。
 *
 * 🔴 **用 `updatedBy` / `updatedAt`，不要图省事换成 `createdBy`**：队长与管理员能改
 *   别人建的记录（`canMaintainActivity`），「最后改的人」经常不是创建人——拿创建人拼这句话
 *   **在最该说清楚的那种情况下恰好是错的**，而且不会报任何错。
 * ⚠ `updatedBy` **可能为 null**：`005_activity_updated_by.sql` 改表前就存在的老记录
 *   没有更新人可考（回填成创建人是编造）。那时回落成「创建人 X · 录入于 …」，
 *   **不要拼出「（已删除人员）更新于 …」**。
 * ⚠ 时间 `slice(0, 16)` 切到分钟；秒对用户没有意义。
 */
export function signatureText(a: Activity, nameOf: (id: string) => string): string {
  return a.updatedBy
    ? `${nameOf(a.updatedBy)} 更新于 ${a.updatedAt.slice(0, 16)}`
    : `创建人 ${nameOf(a.createdBy)} · 录入于 ${a.createdAt.slice(0, 16)}`;
}

export function viewModal(a: Activity, d: { names: Record<string, string> }): void {
  const nameOf = (id: string): string => d.names[id] ?? '（已删除人员）';
  const t = meta.teamById(a.teamId);

  openModal({
    title: `打卡详情 · ${t?.name ?? '未知队伍'}`,
    /* ⚠ 纯文本，openModal 会 esc()。
       🔴 **右上角只写打卡日期**（2026-09-09 用户第二轮）：原先是「创建人 X · 打卡时间 …」，
         两处都改了——① 创建人那半段挪到左下角的署名里，标题栏不再挤两件事；
         ② 时间用 `a.date`（**录入人手填的活动日期**），不是 `a.createdAt`（录入时间）。
         补录很常见——上周三的活动今天才录，写 `createdAt` 会把它说成「今天打卡」，
         **数字没错，说的却是另一件事**。
       ⚠ `createdAt` 只作审计留在库里，**不要"顺手"把它显示回来**；全站同一条口径，
         矩阵「最近打卡」列也是活动日期（后端 `shared/activity.ts` 的 `lastActDate()`）。 */
    titleMeta: `打卡日期 ${a.date}`,
    size: 'md',
    body: viewBodyHtml(a, d.names),
    /* 左下角署名，口径见上面的 `signatureText()`。
       ⚠ 复用录入弹窗那个 `.lead-l`（`08_common.css` 里 `margin-right:auto`，
         所以它被推到左边、按钮留在右边），**不要为它新造一个类**。
       ⚠ `footer` 是**原样插入的 HTML**（不像 `titleMeta` 会自动 esc），姓名必须自己 `esc()`。 */
    footer: `<span class="lead-l">${esc(signatureText(a, nameOf))}</span>
            <button class="btn" data-mclose>关闭</button>
            ${when(canMaintain(a), '<button class="btn primary" data-ckedit>编辑这条记录</button>')}`,
    onMount(root) {
      root.querySelector('[data-ckedit]')?.addEventListener('click', () => {
        closeModal(true);
        void formModal(a);
      });
      root.addEventListener('click', (e) => {
        const el = e.target;
        if (!(el instanceof Element)) return;
        const box = el.closest<HTMLElement>('[data-cklb]');
        if (box) lightbox(a, Number(box.dataset.cklb));
      });
    },
  });
}

/* ===================== 大图灯箱 ===================== */

/**
 * 照片放大（2026-08-27 新增：详情弹窗里原来只有 88×88 的缩略图，看不清）。
 *
 * ⚠ **不走 `openModal`**：它带标题栏 / 页脚 / 白底卡片，而灯箱要的是"整屏只剩这张图"。
 *   代价是 Esc 与焦点得自己管 —— 见下面两条。
 * 🔴 键盘监听挂在 **document 的捕获阶段**并 `stopPropagation()`：
 *   `shared/events.ts` 的 Esc 是冒泡阶段的，不拦住的话按一下 Esc 会把**底下的详情弹窗**关掉，
 *   而用户以为自己只是收起了大图。
 * ⚠ 大图用**原图**（不带变体）：这里就是为了看清楚，缩略图的 `t` 变体长边只有 176。
 */
function lightbox(a: Activity, start: number): void {
  if (!a.photos.length) return;
  let i = Math.min(Math.max(start, 0), a.photos.length - 1);

  const box = document.createElement('div');
  box.className = 'ck-lb';
  box.innerHTML = `<button class="x" data-lbx aria-label="关闭大图">${IC.x}</button>
    <button class="nav p" data-lbp aria-label="上一张">‹</button>
    <img alt="" decoding="async">
    <button class="nav n" data-lbn aria-label="下一张">›</button>
    <div class="cap"></div>`;
  const img = box.querySelector('img') as HTMLImageElement;
  const cap = box.querySelector('.cap') as HTMLElement;
  const multi = a.photos.length > 1;
  box.querySelectorAll('.nav').forEach((b) => {
    if (!multi) b.setAttribute('hidden', '');
  });

  const paint = (): void => {
    const p = a.photos[i];
    if (!p) return;
    /* 🔴 大图必须取 `o` 变体（桶里那张原样的、长边 2048 的图）。
         2026-09-09 修：这里原来用默认变体 `c`，那是**卡片封面的 720px 缩图**——
         全屏铺开等于把 720 拉到 1400+，糊得一塌糊涂，而且完全看不出是取错了变体。
       ⚠ 别为了省流量改回 `c`：看大图这个动作本来就是为了看清楚。 */
    img.src = uploadApi.imgUrl(p.objectKey, 'o');
    img.alt = `照片 ${i + 1}`;
    cap.textContent = multi ? `${i + 1} / ${a.photos.length}` : '';
  };
  const step = (n: number): void => {
    i = (i + n + a.photos.length) % a.photos.length;
    paint();
  };

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
    } else if (e.key === 'ArrowLeft') {
      e.stopPropagation();
      step(-1);
    } else if (e.key === 'ArrowRight') {
      e.stopPropagation();
      step(1);
    }
  };
  function close(): void {
    document.removeEventListener('keydown', onKey, true);
    box.classList.add('closing');
    setTimeout(() => box.remove(), 140);
  }

  box.addEventListener('click', (e) => {
    const el = e.target;
    if (!(el instanceof Element)) return;
    if (el.closest('[data-lbp]')) return step(-1);
    if (el.closest('[data-lbn]')) return step(1);
    // 点图片本身不关，点四周的暗底才关——与"点遮罩不关弹窗"不冲突：这里没有可丢失的输入
    if (el.closest('[data-lbx]') || el === box) close();
  });
  document.addEventListener('keydown', onKey, true);
  paint();
  document.body.appendChild(box);
  box.querySelector<HTMLElement>('[data-lbx]')?.focus();
}

/**
 * 「一键打卡」的唯一入口（2026-08-20 变更 ②.2：首页放一个按钮直接开新增弹窗）。
 * ⚠ 弹窗本体只有一份（`formModal`）：首页与打卡页共用，保存成功后都走 `refreshCurrent()`，
 *   所以在首页保存完，首页的「最近活动」与 Hero 会自动跟着刷新。
 */
export const openNewCheckin = (): void => void formModal(null);

/** 字节 → MB 文案。⚠ 只用于进度条，别拿去做任何口径计算（纪律 2：前端不算业务口径） */
const mb = (n: number): string => `${(n / 1048576).toFixed(1)}MB`;

/* ===================== 录入 / 编辑弹窗 ===================== */

interface Draft {
  teamId: string;
  date: string;
  summary: string;
  memberIds: string[];
  guests: Guest[];
  /** 已签发的对象键 */
  photoKeys: string[];
  /**
   * **本次会话内新选那几张**的 blob 地址（`URL.createObjectURL()`），⚠ 刷新即无。
   * ⚠ 已入库的照片**不进这里**：它们直接走 `uploadApi.imgUrl(key,'t')`，不需要先问一趟地址
   *   （2026-08-23 图片提速，原来那次补拉已删）。
   * ⚠ 它**不参与 `dirty()` 比对**（见下面 `init`），预览地址不会让表单变成"有未保存改动"。
   */
  localUrls: Record<string, string>;
  cover: number;
}

/**
 * 新增 / 编辑打卡（`a` 为 null 表示新增）。
 * ⚠ 弹窗里的改动先落在草稿上，点保存才发请求——一期同款。
 */
async function formModal(a: Activity | null): Promise<void> {
  const isNew = a === null;
  const u = meta.me();
  /* 🔴 **可选队伍 = 服务端真的会放行的那些**（`auth/guard.ts` 的 `canAccessTeam`）：
       管理员全营，其他人只有自己所在的在用队伍。
     2026-09-09 修：这里原来给所有人列全营 13 支队并默认选中 T01，正式队员一打开弹窗，
     候选人接口就拿别人的队伍去问，服务端回 403 → 顶上弹一句「只能操作本队数据」，
     人员框还空着写「该队暂无可选人员」。**那句提示不是要去掉的文案，是不该出现的报错**——
     队伍选对了它自然就没了。 */
  const all = meta.activeTeams().slice().sort((x, y) => (x.code < y.code ? -1 : 1));
  const teams = u?.isAdmin ? all : all.filter((t) => u?.teamIds.includes(t.id));
  if (isNew && !teams.length) {
    return void toast(u?.isAdmin ? '当前没有生效的队伍，无法新增打卡' : '你还没有加入任何在用队伍，无法新增打卡');
  }
  /** 只有一支可选时不给下拉：没得选的选择器只会让人以为还能换 */
  const lockTeam = isNew && teams.length <= 1;

  const d: Draft = {
    teamId:
      a?.teamId ??
      // ⚠ 只有当筛选栏选中的队**在自己可选范围内**时才沿用它，否则回落到第一支可选队
      (state.ck.team && teams.some((t) => t.id === state.ck.team) ? state.ck.team : teams[0]?.id ?? ''),
    // ⚠ 默认值用**服务端的今天**（meta 缓存），不是 new Date()（前端纪律 3）
    date: a?.date ?? meta.today(),
    summary: a?.summary ?? '',
    memberIds: a ? [...a.memberIds] : [],
    guests: a ? a.guests.map((g) => ({ ...g })) : [],
    photoKeys: a ? a.photos.map((p) => p.objectKey) : [],
    localUrls: {},
    cover: a?.cover ?? 0,
  };
  const init = JSON.stringify({ ...d, localUrls: {} });


  /* 正式参与人候选：**本队正式成员 + 本队关联的志愿者 / 内外部导师**，不含管理员与停用。
     ⚠ 口径由服务端给（`GET /api/persons/candidates`，W2-3 已落地），前端不自己筛。 */
  let cands: Person[] = [];
  async function loadCands(): Promise<void> {
    if (!d.teamId) return;
    try {
      cands = await personApi.candidates(d.teamId);
    } catch (e) {
      cands = [];
      toast(errText(e));
    }
  }
  await loadCands();

  /**
   * 候选人列表。
   * 🔴 **2026-09-09 用户拍板：不按人员类型分组，也不在行里标类型。**
   *   填这张表的人只需要回答「谁参加了」——正式成员 / 志愿者 / 导师的区分对他没有用，
   *   四个分组标题还把这个滚动区一屏能看到的人数压掉了三分之一。
   * ⚠ 顺序仍用后端 `candidatesOf` 给的（正式成员 → 志愿者 → 导师 → 外部导师），
   *   前端不重排：本队正式成员排在最前，是填表时最常选的那一批。
   * ⚠ 行右侧统一显示**部门**（外部导师的「部门」记的是所属单位）。它在这里的作用是
   *   **区分同名的人**，不是类型标签——写回 `TYPE_LABEL` 等于把刚去掉的分类又贴回每一行。
   * ⚠ `12_checkins.css` 的 `.ck-pg`（分组标题）从此匹配不到元素，但那一段与冻结的一期
   *   逐字节一致，**不去删它**（同 14 段里那些 `.pp-*`）。
   */
  const peopleHtml = (): string => {
    if (!cands.length) return '<div class="hint">该队暂无可选人员，请先在队伍管理里维护成员</div>';
    return cands
      .map(
        (p) => `<label class="ck-p"><input type="checkbox" data-ckm="${esc(p.id)}" ${
          d.memberIds.includes(p.id) ? 'checked' : ''
        }><span>${esc(p.name)}</span><s>${esc(p.dept)}</s></label>`,
      )
      .join('');
  };

  const guestsHtml = (): string =>
    (d.guests.length
      ? d.guests
          .map(
            (g, i) => `<div class="ck-g">
      <input class="gn" type="text" maxlength="12" value="${esc(g.name)}" data-ckg="name|${i}" placeholder="姓名">
      <input class="go" type="text" maxlength="20" value="${esc(g.org)}" data-ckg="org|${i}" placeholder="所属单位">
      <button class="gx" data-ckx="${i}" aria-label="移除这一行">${IC.x}</button>
    </div>`,
          )
          .join('')
      : '') +
    `<button class="tbtn" data-ckadd>＋ ${d.guests.length ? '添加一行' : '添加临时参与人'}</button>`;

  const picsHtml = (): string => {
    const cells = d.photoKeys
      .map((key, i) => {
        // ⚠ 新选的图走 blob 预览（还没入库，`/api/img` 取不到）；已入库的直接拼地址
        const u = d.localUrls[key] ?? uploadApi.imgUrl(key, 't');
        return `<div class="ck-th ${i === d.cover ? 'on' : ''}">
        <div class="box">${IC.pic}<img src="${esc(u)}" alt="照片 ${i + 1}"
          loading="lazy" decoding="async" data-imgfb>
          <button class="x" data-ckpx="${i}" aria-label="移除这张">${IC.x}</button>
          ${i === d.cover ? '<span class="cv">封面</span>' : ''}</div>
        ${
          i === d.cover
            ? '<span class="tbtn" style="color:var(--ink-4);text-decoration:none">当前封面</span>'
            : `<button class="tbtn" data-ckcv="${i}">设为封面</button>`
        }
      </div>`;
      })
      .join('');
    return `<div class="ck-up">
        <button class="btn" data-ckpick>${IC.plus}选择图片</button>
        <input type="file" id="ck-file" accept="image/*" multiple>
        <p>最多 ${MAX_PIC} 张。${
          meta.uploadReady()
            ? '请优先使用<b>横版照片</b>，以实现最佳展示效果。'
            : '<b>图片存储未配置</b>：本次选的图只在当前页面预览，<b>刷新后看不到缩略图</b>，' +
              '但张数、顺序与封面会正常保存；配置好之后新存的图会正常显示。'
        }</p>
      </div>
      <!-- ⚠ 进度条要留在 paint('pics') 会重绘的这块【之内】，但上传期间【不会】发生重绘
           （paint 只在压完之后调一次），所以整段上传过程里它是稳定存在的 -->
      <div class="ck-prog" id="ck-prog" hidden>
        <div class="bar"><i></i></div><span class="t"></span>
      </div>
      <div class="ck-thumbs">${cells}</div>
      <div class="hint" style="margin-top:8px">当前 <b class="num">${d.photoKeys.length}</b> 张</div>`;
  };

  const body = `
    <div class="ck-2">
      <div class="fld" data-f="team"><label>所属队伍<span class="req">*</span></label>
        ${
          isNew && !lockTeam
            ? `<select id="ck-f-team">${teams
                .map((t) => opt(t.id, d.teamId, `${t.code} ${t.name}`))
                .join('')}</select>`
            : `<input type="text" readonly value="${esc(teamLabel(meta.teamById(d.teamId)))}">
               ${
                 isNew
                   ? ''
                   : '<div class="hint">编辑时不可改队伍——换队会串统计，要换请删除后重建</div>'
               }`
        }
        <div class="err"></div></div>
      <div class="fld" data-f="date"><label>活动日期<span class="req">*</span></label>
        <input type="date" id="ck-f-date" max="${esc(meta.today())}" value="${esc(d.date)}">
        <div class="err"></div></div>
    </div>
    <div class="fld" data-f="sum"><label>活动摘要<span class="req">*</span></label>
      <textarea id="ck-f-sum" class="ck-sum" rows="3" maxlength="${SUM_MAX}"
        placeholder="说说本次活动都做了什么值得记录的事情吧">${esc(d.summary)}</textarea>
      <div class="hint ck-sumn"><b class="num" id="ck-f-sumn">${d.summary.length}</b> / ${SUM_MAX} 字</div>
      <div class="err"></div></div>
    <div class="ck-2">
      <div class="fld" data-f="members"><label>正式参与人员<span class="req">*</span></label>
        <div class="ck-people" id="ck-f-people">${peopleHtml()}</div>
        <div class="err"></div></div>
      <div class="fld" data-f="guests"><label>临时参与人</label>
        <div id="ck-f-guests">${guestsHtml()}</div>
        <div class="err"></div></div>
    </div>
    <div class="fld" data-f="pics"><label>照片<span class="req">*</span></label>
      <div id="ck-f-pics">${picsHtml()}</div>
      <div class="err"></div></div>`;

  const rec = openModal({
    title: isNew ? '新增打卡记录' : '编辑打卡记录',
    // 2026-08-27 由 lg(880) 收到 md(760)：两列排得下，880 在 13 寸屏上横铺得像整页
    size: 'md',
    body,
    footer: `<span class="lead-l">参与人次 <b class="num" id="ck-f-pax">${
      d.memberIds.length + d.guests.length
    }</b> 人</span>
            <button class="btn" data-mclose>取消</button>
            <button class="btn primary" data-save>${isNew ? '保存打卡' : '保存修改'}</button>`,
    dirty: () => JSON.stringify({ ...d, localUrls: {} }) !== init,
    onMount(root) {
      const q = <T extends Element>(sel: string): T | null => root.querySelector<T>(sel);
      const paint = (k?: 'people' | 'guests' | 'pics'): void => {
        if (!k || k === 'people') {
          const el = q('#ck-f-people');
          if (el) el.innerHTML = peopleHtml();
        }
        if (!k || k === 'guests') {
          const el = q('#ck-f-guests');
          if (el) el.innerHTML = guestsHtml();
        }
        if (!k || k === 'pics') {
          const el = q('#ck-f-pics');
          if (el) el.innerHTML = picsHtml();
        }
        const px = q('#ck-f-pax');
        if (px) px.textContent = String(d.memberIds.length + d.guests.length);
      };

      q<HTMLSelectElement>('#ck-f-team')?.addEventListener('change', (e) => {
        d.teamId = (e.target as HTMLSelectElement).value;
        // ⚠ 换队要清空已选参与人：候选范围变了，留着会提交出"不是本队的人"
        d.memberIds = [];
        void loadCands().then(() => paint('people'));
      });
      q<HTMLInputElement>('#ck-f-date')?.addEventListener('change', (e) => {
        d.date = (e.target as HTMLInputElement).value;
      });
      q<HTMLTextAreaElement>('#ck-f-sum')?.addEventListener('input', (e) => {
        d.summary = (e.target as HTMLTextAreaElement).value;
        const n = q('#ck-f-sumn');
        if (n) n.textContent = String(d.summary.length);
      });

      root.addEventListener('change', (e) => {
        const el = e.target;
        if (!(el instanceof HTMLInputElement)) return;
        const m = el.dataset.ckm;
        if (m) {
          if (el.checked) {
            if (!d.memberIds.includes(m)) d.memberIds.push(m);
          } else {
            d.memberIds = d.memberIds.filter((x) => x !== m);
          }
          const px = q('#ck-f-pax');
          if (px) px.textContent = String(d.memberIds.length + d.guests.length);
          return;
        }
        if (el.id === 'ck-file') void pickFiles(el);
      });

      root.addEventListener('input', (e) => {
        const el = e.target;
        if (!(el instanceof HTMLInputElement)) return;
        const g = el.dataset.ckg;
        if (!g) return;
        const [field, idx] = g.split('|');
        const row = d.guests[Number(idx)];
        if (row && (field === 'name' || field === 'org')) row[field] = el.value;
      });

      root.addEventListener('click', (e) => {
        const el = e.target;
        if (!(el instanceof Element)) return;
        if (el.closest('[data-ckpick]')) return void q<HTMLInputElement>('#ck-file')?.click();
        if (el.closest('[data-ckadd]')) {
          d.guests.push({ name: '', org: '' });
          return paint('guests');
        }
        const gx = el.closest<HTMLElement>('[data-ckx]');
        if (gx) {
          d.guests.splice(Number(gx.dataset.ckx), 1);
          return paint('guests');
        }
        const px = el.closest<HTMLElement>('[data-ckpx]');
        if (px) {
          const i = Number(px.dataset.ckpx);
          d.photoKeys.splice(i, 1);
          if (d.cover >= d.photoKeys.length) d.cover = Math.max(0, d.photoKeys.length - 1);
          return paint('pics');
        }
        const cv = el.closest<HTMLElement>('[data-ckcv]');
        if (cv) {
          d.cover = Number(cv.dataset.ckcv);
          return paint('pics');
        }
        if (el.closest('[data-save]')) void submit(root);
      });

      /**
       * 选图 → **压缩（并发 3）** → 签发对象键 → 直传 COS → 本地预览。
       *
       * 🔴 **全程必须有进度反馈**（2026-08-22 用户反馈「点了没反应、卡住」）：
       *   压缩 9 张约 3—4 秒、上传又要十几秒，中间界面一动不动就会被当成页面死了。
       *   ⚠ 实测**主线程并没有被堵**（压缩的解码与编码都在主线程之外，见 `shared/image.ts` 头部），
       *     所以这里不需要 Web Worker，进度条也确实画得动——**别再往 Worker 方向修**。
       * ⚠ 期间把按钮禁掉：不禁的话用户会连点，重复签发一堆对象键。
       * ⚠ 压缩失败不会抛（`shared/image.ts` 内部回退原图），所以 catch 只兜网络错。
       */
      async function pickFiles(input: HTMLInputElement): Promise<void> {
        const files = [...(input.files ?? [])].filter((f) => f.type.startsWith('image/'));
        input.value = '';
        if (!files.length) return;
        const left = MAX_PIC - d.photoKeys.length;
        if (left <= 0) return void toast(`最多上传 ${MAX_PIC} 张，已选 ${d.photoKeys.length} 张`);
        const use = files.slice(0, left);
        if (files.length > left) toast(`最多上传 ${MAX_PIC} 张，本次只保留了前 ${left} 张`);

        const btn = q<HTMLButtonElement>('[data-ckpick]');
        const box = q<HTMLElement>('#ck-prog');
        const fill = q<HTMLElement>('#ck-prog .bar i');
        const text = q<HTMLElement>('#ck-prog .t');
        const show = (ratio: number, label: string): void => {
          if (box) box.hidden = false;
          if (fill) fill.style.width = `${Math.round(ratio * 100)}%`;
          if (text) text.textContent = label;
        };
        if (btn) btn.disabled = true;
        /* ⚠ 起手就报「压缩中 0/N」而不是「准备中」：并发 3 时头一批要 1 秒多才有第一个完成，
           这段时间条子必然停在 0%——**必须让文案说清楚它在干什么**，否则和原来的"没反应"没区别。
           ⚠ 别为此加不确定态的循环动画：红线 1 的白名单只有登录页那三处，内页一条都不许有。 */
        show(0, `压缩中 0/${use.length} 张`);

        try {
          const r = await uploadApi.uploadFiles(d.teamId, use, (p) =>
            show(
              p.ratio,
              p.phase === 'compress'
                ? `压缩中 ${p.done}/${p.total} 张`
                : `上传中 ${mb(p.done)} / ${mb(p.total)}`,
            ),
          );
          r.objectKeys.forEach((key, i) => {
            d.photoKeys.push(key);
            /* ⚠ 用**压缩后**的文件建 blob URL：拿原文件建等于把 3MB 又在内存里挂一份，
               9 张就是 27MB，手机上直接把页面挤掉。
               ⚠ blob URL 只在当前页面有效，刷新即无——mock 模式下这是唯一的预览来源 */
            d.localUrls[key] = URL.createObjectURL((r.files[i] ?? use[i]) as File);
          });
          // ⚠ 先报完成再重绘：paint 会把进度条连同这一块一起换掉，等于自动收起
          show(1, '完成');
          paint('pics');
        } catch (err) {
          // ⚠ 失败时**不要** paint：重绘会把进度条抹掉，用户只看到一个 toast 一闪而过
          if (box) box.hidden = true;
          if (btn) btn.disabled = false;
          toast(errText(err));
        }
      }
    },
  });

  function mark(root: ParentNode, key: string, msg: string): void {
    root.querySelectorAll('.fld').forEach((f) => f.classList.remove('bad'));
    const f = root.querySelector(`[data-f="${key}"]`);
    if (!f) return void toast(msg);
    f.classList.add('bad');
    const err = f.querySelector('.err');
    if (err) err.textContent = msg;
    f.scrollIntoView({ block: 'center' });
  }

  async function submit(root: ParentNode): Promise<void> {
    /* ⚠ 前端只拦「明显没填」，**不是校验**：日期越界、摘要超长、参与人越界、
       临时参与人同填同空、照片张数全部由服务端判，文案直接用服务端返回的（红线 4）。 */
    if (!d.date) return mark(root, 'date', '请选择活动日期');
    if (!d.summary.trim()) return mark(root, 'sum', '请填写活动摘要');
    if (!d.memberIds.length) return mark(root, 'members', '至少选择 1 名正式参与人员');
    if (!d.photoKeys.length) return mark(root, 'pics', '至少上传 1 张照片');

    const btn = root.querySelector<HTMLButtonElement>('[data-save]');
    if (btn) btn.disabled = true;
    const input = {
      teamId: d.teamId,
      date: d.date,
      summary: d.summary.trim(),
      memberIds: d.memberIds,
      guests: d.guests,
      photoKeys: d.photoKeys,
      cover: d.cover,
    };
    try {
      const saved = isNew
        ? await actApi.createActivity(input)
        : await actApi.updateActivity(a?.id as string, input);
      closeModal(true);
      refreshCurrent();
      toast(
        `${isNew ? '已新增' : '已更新'}打卡记录「${cut(saved.summary, 14)}」，参与 ${
          saved.memberIds.length + saved.guests.length
        } 人次`,
      );
    } catch (e) {
      // ⚠ 错误留在弹窗里，不 toast：填的内容还在，人要在原地改
      mark(root, 'sum', errText(e));
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  void rec;
}

/* ===================== 删除 ===================== */

function deleteActivity(a: Activity): void {
  const t = meta.teamById(a.teamId);
  const pax = a.memberIds.length + a.guests.length;
  confirmModal({
    title: '删除打卡记录',
    okText: '删除记录',
    danger: true,
    html: `确认删除 <b>${esc(cut(a.summary, 24))}</b>？<br>
      删除后 ${esc(t?.name ?? '该队')} 的 <b>1</b> 条活动与 <b>${pax}</b> 人次将从统计中扣减，此操作不可撤销。`,
    onOk: () => {
      void (async () => {
        try {
          await actApi.deleteActivity(a.id);
          refreshCurrent();
          toast(`已删除打卡记录「${cut(a.summary, 14)}」`);
        } catch (e) {
          toast(errText(e));
        }
      })();
    },
  });
}

/* ===================== 筛选（只改 state，再让 render 重取） ===================== */

let searchTimer: ReturnType<typeof setTimeout> | null = null;
let refocusSearch = false;

function onSearchInput(value: string): void {
  state.ck.q = value;
  refocusSearch = true;
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => refreshCurrent(), 250);
}

/* ===================== 页面模块 ===================== */

export const checkinsPage = definePage<CheckinsData>({
  skeleton: 'cards',

  async load({ route, signal }) {
    // 队伍来自 hash（进入时预置一次），之后改下拉不回写 hash——它只是筛选条件（守卫已处理）
    const teamId = route.view === 'checkins' ? (route.team ?? state.ck.team) : state.ck.team;
    const q = { teamId: teamId || '', span: state.ck.span as '14' | 'cycle' | 'all', q: state.ck.q };
    const [list, stats, people] = await Promise.all([
      actApi.listActivities(q, { signal }),
      // ⚠ 页头是【全营】口径，不随队伍筛选变——它回答的是"训练营整体活跃度"
      actApi.activityStats(undefined, { signal }),
      // 参与人姓名表：详情弹窗要用；⚠ 走通用列表（登录可读），打卡记录是全营公开的
      personApi.listPersons({}, { signal }),
    ]);

    /* ⚠ **封面不再在这里换地址**（2026-08-23 图片提速）：地址由 `cardHtml` 里的
       `uploadApi.imgUrl()` 就地拼，`load()` 少一个往返，封面跟首屏 HTML 一起出来。
       取不到图（COS 开通前存的记录、对象被误删）由 `data-imgfb` 兜到占位格。 */
    return {
      list,
      stats,
      names: Object.fromEntries(people.items.map((p) => [p.id, p.name])),
    };
  },

  html: (d) => checkinsHtml(d),

  mounted() {
    const main = document.querySelector('#main');
    if (main && state.ck.scroll) main.scrollTop = state.ck.scroll;
    if (!refocusSearch) return;
    refocusSearch = false;
    const el = document.querySelector<HTMLInputElement>('#ck-q');
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  },

  onEvent(e, d) {
    const t = e.target;
    if (!(t instanceof Element)) return;

    if (e.type === 'input' && t.id === 'ck-q') {
      return onSearchInput((t as HTMLInputElement).value);
    }
    if (e.type === 'change' && (t.id === 'ck-team' || t.id === 'ck-span')) {
      const v = (t as HTMLSelectElement).value;
      if (t.id === 'ck-team') state.ck.team = v;
      else state.ck.span = v;
      // ⚠ 筛选变了就回顶：结果换了一批，停在原来的滚动位置没有意义（一期同款）
      state.ck.scroll = 0;
      document.querySelector('#main')?.scrollTo?.({ top: 0 });
      return refreshCurrent();
    }
    if (e.type !== 'click') return;

    const op = t.closest<HTMLElement>('[data-ck]')?.dataset.ck;
    if (!op) return;
    const [kind, id] = op.split('|');
    const act = id ? d.list.items.find((x) => x.id === id) : null;

    if (kind === 'new') return void formModal(null);
    if (kind === 'clearq') {
      state.ck.q = '';
      return refreshCurrent();
    }
    if (kind === 'clearf') {
      state.ck.team = '';
      state.ck.span = '14';
      state.ck.q = '';
      state.ck.scroll = 0;
      return refreshCurrent();
    }
    if (!act) return;
    if (kind === 'view') return viewModal(act, d);
    if (kind === 'edit') return void formModal(act);
    if (kind === 'del') return deleteActivity(act);
  },
});
